import { Auth } from 'aws-amplify';
import jwt from 'jwt-decode';
import { getUser } from '../services/user';
import axios from 'axios';
import { Logger } from '@elyx-code/common-ts-utils';
import { envConfig } from '../config';

// How to refresh expired tokens:
// https://github.com/aws-amplify/amplify-js/issues/2560#issuecomment-481707972

// This variable is to cache the access token
let accessToken: string = '';
let accessTokenExpiration: number = 0;

// Change password function
export async function changePassword(oldPassword: string, newPassword: string) {
	const user = await Auth.currentAuthenticatedUser();

	return Auth.changePassword(user, oldPassword, newPassword);
}

// Sign up function
export async function signUp(email: string, password: string) {
	return Auth.signUp({
		username: email, // Use email as the username
		password,
		attributes: {
			email,
		},
	});
}

// Sign in function
export async function signIn(email: string, password: string) {
	return Auth.signIn(email, password); // Use email as the username
}

// Confirm sign up function
export async function confirmSignUp(email: string, code: string) {
	return await Auth.confirmSignUp(email, code);
}

// Sign out function
export async function signOut() {
	await Auth.signOut();
	accessToken = '';
}

// Send email confirmation code function
export async function resendConfirmationCode(email: string) {
	return await Auth.resendSignUp(email);
}

// Forgot password function
export async function forgotPassword(email: string) {
	return Auth.forgotPassword(email);
}

// Forgot password submit function
export async function forgotPasswordSubmit(
	email: string,
	code: string,
	new_password: string,
) {
	return Auth.forgotPasswordSubmit(email, code, new_password);
}

// Get the authenticated user
export async function getCurrentSession() {
	try {
		return await Auth.currentSession();
	} catch (error) {
		Logger.log('Error getting current user session: ', error);
	}
}

export async function getAccessToken(): Promise<string | null> {
	try {
		// Check if the access token is expired, by comparing the current timestamp with 'accessTokenExpiration'
		// If so, set the current token to an empty value, then call this function (recursion), so it fetches a new token
		// We check if the token expires within the next 5 minutes to have some buffer
		if (accessToken) {
			if (accessTokenExpiration < Date.now() + 5 * 60 * 1000) {
				accessToken = '';
				accessTokenExpiration = 0;

				return await getAccessToken();
			} else {
				return accessToken;
			}
		}

		// Refreshing access token

		const session: any = await getCurrentSession();

		if (session?.accessToken?.jwtToken) {
			accessToken = session.accessToken.jwtToken;
			accessTokenExpiration = session.accessToken.payload.exp * 1000;
			return accessToken;
		}

		const user = await Auth.currentAuthenticatedUser();

		if (!user) {
			accessToken = '';
			accessTokenExpiration = 0;

			return null;
		} else if (user.token) {
			// Decode token to read the expiration date
			accessToken = user.token;
			const decodedToken: any = jwt(accessToken);
			accessTokenExpiration = decodedToken.exp * 1000;

			return accessToken;
		} else if (user.signInUserSession.accessToken) {
			accessToken = user.signInUserSession.accessToken.jwtToken;
			const decodedToken: any = jwt(accessToken);
			accessTokenExpiration = decodedToken.exp * 1000;

			return accessToken;
		}

		accessToken = '';
		accessTokenExpiration = 0;

		return null;
	} catch (error) {
		Logger.log('Error getting access token: ', error);

		return null;
	}
}

export async function getRefreshToken(): Promise<string | null> {
	try {
		const session: any = await Auth.currentSession();
		return session.refreshToken.token;
	} catch (error) {
		return null;
	}
}

// Get email from session user, either in the cognito uer session format, or the federated identity format
function getEmailFromSessionUser(
	sessionUser:
		| {
				attributes: { email: string };
		  }
		| { email: string },
) {
	// @ts-ignore
	const cognitoEmail = sessionUser?.attributes?.email;

	if (cognitoEmail) {
		return cognitoEmail;
	}

	// @ts-ignore
	if (sessionUser?.email) {
		// @ts-ignore
		return sessionUser.email;
	}
}

// Get current user
export async function getCurrentUser(): Promise<any> {
	const session = await Auth.currentAuthenticatedUser();

	if (!session) {
		return null;
	}

	const email = getEmailFromSessionUser(session);

	const user = await getUser({
		email,
	});

	return user;
}

// Uses the 'getCurrentUser' function to get the current user,
// or redirects to the login page if the user is not logged in or error occurs
export async function getCurrentUserOrLogoutRedirect(): Promise<any> {
  const triggerRedirect = async () => {
    const baseUrl = window.location.origin;
    let currentPath = window.location.pathname + window.location.search;
    
    // Prevent redirect loops or redirecting back to the auth page itself
    if (currentPath.startsWith('/auth')) {
      currentPath = '/gallery';
    }

    const newPath = `/auth/sign-in?redirectTo=${encodeURIComponent(currentPath)}`;
    window.location.replace(baseUrl + newPath);
  };

  try {
    const user = await getCurrentUser();

    if (!user) {
      await signOut();
      await triggerRedirect();
      return null;
    }

    return user;
  } catch (error) {
    try {
      await signOut();
    } catch (err) {
      Logger.log('Error signing out: ', err);
    }
    Logger.log('Error getting current user: ', error);
    await triggerRedirect();
    return null;
  }
}

// Exchange Google token for temporary AWS credentials
const getAWSCredentialsFromGoogleCreds = async (credential: string) => {
	const token = jwt(credential) as any;

	const user = {
		email: token.email,
		name: token.given_name,
		family_name: token.family_name,
	};

	const creds = await Auth.federatedSignIn(
		'google',
		{ token: credential, expires_at: token.exp },
		user,
	);

	const accessToken = await getAccessToken();

	try {
		// We save this user on our database
		await axios.post(
			`${envConfig.API_BASE_URL}/user-service/v1/user/sync`,
			{
				email: user.email,
				name: user.name,
				lastName: user.family_name,
				identityProviderUserId: creds.identityId,
				identityProvider: 'google',
				picture: token.picture,
			},
			{
				headers: {
					Authorization: 'Bearer ' + accessToken,
				},
			},
		);
	} catch (error) {
		Logger.log('Error saving user on our database: ', error);
	}
};

export const createGoogleSocialSignInButtons = () => {
	// @ts-ignore
	if (window.google && window.google?.accounts) {
		// Options for Google buttons can be found here:
		// https://developers.google.com/identity/gsi/web/reference/js-reference#theme
		// @ts-ignore
		window.google.accounts.id.renderButton(
			document.getElementById('google-sign-up-button'),
			{ theme: 'outline', size: 'medium' },
		);

		// @ts-ignore
		window.google.accounts.id.renderButton(
			document.getElementById('google-sign-in-button'),
			{ theme: 'outline', size: 'medium' },
		);
	}
};

// Initialize Google client and render Google button
const initGsi = () => {
	// @ts-ignore
	if (window.google && window.google?.accounts) {
		// @ts-ignore
		window.google.accounts.id.initialize({
			client_id:
				'481480200529-1oe59ol3snikmj6l9dksirjabooupohd.apps.googleusercontent.com',
			callback: (response: any) => {
				getAWSCredentialsFromGoogleCreds(response.credential);
			},
		});

		createGoogleSocialSignInButtons();
	}
};

// Load google client
export const initGoogleSocialSignIn = () => {
	// @ts-ignore
	if (!window.google && !window.google?.accounts) {
		const script = document.createElement('script');

		script.src = 'https://accounts.google.com/gsi/client';
		script.async = true;
		script.defer = true;
		script.onload = initGsi;

		document.body.appendChild(script);
		return;
	}
	// @ts-ignore
	if (!!window.google?.accounts) {
		initGsi();
		return;
	}
};
